import { Signal, WritableSignal } from '@angular/core';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

export interface FilterConfig<T> extends Record<string, unknown> {
  name: T;
  title?: string;
  location: 'default' | 'additional' | 'secondary' | 'none';
  // When true, this filter will not be auto-opened in the additional section just because it has a prefilled value (e.g. defaults or query params).
  skipAutoActivateWhenValuePresent?: boolean;
  // When true, pin this filter to the top of the More Filters list
  // (ahead of the alphabetical sort of other available filters).
  moreFiltersFirst?: boolean;
  // When true, pin this filter to the bottom of the More Filters list
  // (after the alphabetical sort of other available filters).
  moreFiltersLast?: boolean;
  // When true, clearAllFilters and hasAnyActiveFilter will skip this filter
  // so it cannot be cleared by the user.
  required?: boolean;
  // Keep this filter available when its option list is empty or still loading.
  // Use for components that render a useful empty or prerequisite state.
  showWhenOptionsEmpty?: boolean;
  // When true, the value is neither saved to nor restored from local filter
  // preferences. For state whose source of truth is elsewhere (a saved
  // dashboard posture, the URL): a stale local preference would otherwise
  // restore over the value the page just seeded.
  skipPreferences?: boolean;
  icon?: IconDefinition;
  iconClass?: string;
  filterComponent?: () => Promise<any>;
}

export function getRawValues<
  TValues extends Record<string, WritableSignal<unknown>>,
>(values: TValues) {
  return Object.entries(values).reduce(
    (acc, [key, value]) => {
      acc[key as keyof typeof values] = value();
      return acc;
    },
    {} as Record<keyof TValues, unknown>
  );
}

export function getRawOptions<
  TOptions extends Record<string, WritableSignal<unknown>>,
>(options: TOptions) {
  return Object.entries(options).reduce(
    (acc, [key, value]) => {
      acc[key as keyof typeof options] = value();
      return acc;
    },
    {} as Record<keyof TOptions, unknown>
  );
}

export interface FilterDefinition<
  TValues extends Record<string, WritableSignal<unknown>>,
  TOptions extends Record<string, unknown>,
  TResult,
> {
  configs: Signal<FilterConfig<string>[]>;

  values: TValues;
  /** True when the current values differ from this filter's semantic defaults. */
  hasActiveFilters?: Signal<boolean>;
  /** Restores semantic defaults such as `all` or `active`. */
  resetToDefaults?: () => void;
  /**
   * Re-seeds values that mirror external context rather than a user choice
   * (e.g. the agency scope from the app switcher). Called after any generic
   * reset so clearing chips cannot drop that context.
   */
  reapplyScopeDefaults?: () => void;

  resultsEnabled: Signal<boolean>;
  resultsQueryKey: Signal<string[]>;

  /**
   * Components should query this via tanstack
   */
  getResults: () => Promise<{
    nextPageToken?: string;
    total: number;
    results: TResult[];
  }>;

  optionsQueryKey: Signal<string[]>;

  /**
   * Primary readiness gate for filter UI and, by default, the TanStack options
   * fetch. Most filters use one signal for both: when false, FilterContainer
   * does not call {@link getOptions}.
   *
   * Host pages may also read this to decide when to mount filter chrome (e.g.
   * show the bar once auth is known). That mount decision can be intentionally
   * broader than when dropdown options are safe to load — see
   * {@link optionsFetchEnabled}.
   */
  optionsEnabled: Signal<boolean>;

  /**
   * Optional, stricter gate used only for the TanStack options fetch in
   * FilterContainer. When omitted, {@link optionsEnabled} is used instead.
   *
   * Use when option loading depends on async scope readiness that should not
   * block mounting the filter bar — for example, waiting for payee downline
   * verification before sending payee ids to Elasticsearch while still
   * showing filters as soon as the signed-in user is known.
   *
   * When gating on async readiness, also include that readiness state in
   * {@link optionsQueryKey}. Otherwise TanStack Query may reuse stale or empty
   * cached options across verification-state transitions (e.g. pending →
   * verified) even after {@link optionsFetchEnabled} becomes true.
   */
  optionsFetchEnabled?: Signal<boolean>;

  /**
   * Primarily queried by the FilterContainer
   */
  getOptions: () => Promise<TOptions>;
}

export type UnknownFilterDefinition = FilterDefinition<
  Record<string, WritableSignal<unknown>>,
  Record<string, unknown>,
  unknown
>;
